import React, { useState, useEffect } from "react";
import { 
  Loader2, Wallet, FileText, ArrowUpRight, ArrowDownRight, 
  TrendingUp, Download, PieChart, Receipt, CreditCard, Edit, Trash2
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function AccountingDashboard() {
  const [data, setData] = useState({
    summary: { total_sales: 0, total_costs: 0, total_profit: 0, total_gst: 0, total_received: 0, receivables: 0 },
    invoices: [],
    transactions: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedParty, setSelectedParty] = useState("All");
  
  const [showAddTx, setShowAddTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [newTx, setNewTx] = useState({ party_name: "", type: "OUT", amount: "", bank_account: "", reference: "", notes: "", date: new Date().toISOString().split("T")[0] });
  const [isSubmitting, setIsSubmitting] = useState(false);


  const [dateFilter, setDateFilter] = useState("this_month");
  const [customDates, setCustomDates] = useState({ start: new Date().toISOString().split("T")[0], end: new Date().toISOString().split("T")[0] });

  useEffect(() => {
    if (dateFilter !== "custom") {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      let start_date = null;
      let end_date = null;
      const today = new Date();
      
      if (dateFilter === "this_month") {
        start_date = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end_date = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
      } else if (dateFilter === "last_30_days") {
        const last30 = new Date();
        last30.setDate(today.getDate() - 30);
        start_date = last30.toISOString().split('T')[0];
        end_date = today.toISOString().split('T')[0];
      } else if (dateFilter === "this_quarter") {
        const q = Math.floor(today.getMonth() / 3);
        start_date = new Date(today.getFullYear(), q * 3, 1).toISOString().split('T')[0];
        end_date = new Date(today.getFullYear(), q * 3 + 3, 0).toISOString().split('T')[0];
      } else if (dateFilter === "last_quarter") {
        const q = Math.floor(today.getMonth() / 3) - 1;
        start_date = new Date(today.getFullYear(), q * 3, 1).toISOString().split('T')[0];
        end_date = new Date(today.getFullYear(), q * 3 + 3, 0).toISOString().split('T')[0];
      } else if (dateFilter === "financial_year") {
        const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
        start_date = new Date(year, 3, 1).toISOString().split('T')[0];
        end_date = new Date(year + 1, 2, 31).toISOString().split('T')[0];
      } else if (dateFilter === "custom") {
        start_date = customDates.start;
        end_date = customDates.end;
      }

      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;

      const res = await api.getAccountingReports(params);
      setData(res);
    } catch (error) {
      console.error("Error loading accounting data:", error);
      toast.error("Failed to load accounting data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (!newTx.party_name || !newTx.amount) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...newTx,
        amount: parseFloat(newTx.amount)
      };
      if (editingTx) {
        await api.updateLedgerTransaction(editingTx, payload);
        toast.success("Transaction updated!");
      } else {
        await api.addLedgerTransaction(payload);
        toast.success("Transaction saved!");
      }
      setShowAddTx(false);
      setEditingTx(null);
      setNewTx({ party_name: "", type: "OUT", amount: "", bank_account: "", reference: "", notes: "", date: new Date().toISOString().split("T")[0] });
      loadData();
    } catch (error) {
      console.error("Error saving transaction:", error);
      toast.error("Failed to save transaction");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (tx) => {
    setEditingTx(tx.id);
    setNewTx({
      party_name: tx.party || tx.party_name || tx.customer_name || "",
      type: tx.type,
      amount: tx.amount,
      bank_account: tx.bankAccount || tx.bank_account || "",
      reference: tx.reference || "",
      notes: tx.notes || "",
      date: tx.date || new Date().toISOString().split("T")[0]
    });
    setShowAddTx(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      await api.deleteLedgerTransaction(id);
      toast.success("Transaction deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#ea580c]" />
      </div>
    );
  }

  const { summary, invoices, transactions } = data;

  const indirectExpenses = transactions.filter(tx => tx.type === "EXPENSE");
  
  const baseTxs = activeTab === "expenses" ? indirectExpenses : transactions;
  const uniqueParties = ["All", ...new Set(baseTxs.map(tx => tx.party || tx.customer_name || "Unknown"))];
  const displayTxs = selectedParty === "All" ? baseTxs : baseTxs.filter(tx => (tx.party || tx.customer_name || "Unknown") === selectedParty);

  const indirectCostBreakdown = indirectExpenses.reduce((acc, tx) => {
    const category = tx.reference || "Uncategorized";
    acc[category] = (acc[category] || 0) + parseFloat(tx.amount || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-sans text-3xl font-bold text-slate-800 tracking-tight">Accounting & Reports</h1>
          <p className="font-sans text-slate-500 mt-1">Financial overview, invoices, and ledger</p>
        </div>
        <div className="flex items-center gap-3">
          {dateFilter === "custom" && (
            <div className="flex items-center gap-2">
              <input type="date" value={customDates.start} onChange={e => setCustomDates({...customDates, start: e.target.value})} className="bg-white border border-slate-200 text-slate-800 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#ea580c]" />
              <span className="text-slate-400">-</span>
              <input type="date" value={customDates.end} onChange={e => setCustomDates({...customDates, end: e.target.value})} className="bg-white border border-slate-200 text-slate-800 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#ea580c]" />
              <Button onClick={loadData} size="sm" className="bg-[#ea580c] hover:bg-[#c2410c] text-white rounded-lg">Apply</Button>
            </div>
          )}
          <select 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-white border border-slate-200 text-slate-800 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#ea580c]"
          >
            <option value="this_month">This Month</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="this_quarter">This Quarter</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="financial_year">Financial Year</option>
            <option value="custom">Custom Range</option>
          </select>
          <Button onClick={loadData} variant="outline" className="border-slate-200 text-slate-800 hover:bg-slate-100 bg-slate-50 rounded-xl">
            Refresh Data
          </Button>
          <Button onClick={() => { setActiveTab("expenses"); setShowAddTx(true); setNewTx({...newTx, type: "EXPENSE"}); }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl shadow-md">
            + Record Expense
          </Button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
        {[
          { id: "overview", label: "Overview", icon: PieChart },
          { id: "invoices", label: "Invoices", icon: FileText },
          { id: "ledger", label: "Ledger", icon: Wallet },
          { id: "expenses", label: "Expenses", icon: CreditCard },
          { id: "gst", label: "GST Report", icon: Receipt }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeTab === t.id 
                ? "bg-[#ea580c] text-white shadow-[0_0_15px_rgba(234,88,12,0.3)]" 
                : "bg-slate-50 text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-orange-500" /> Total Sales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">{formatCurrency(summary.total_sales)}</div>
              </CardContent>
            </Card>

            <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <ArrowUpRight className="w-4 h-4 text-green-400" /> Total Profit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">{formatCurrency(summary.total_profit)}</div>
              </CardContent>
            </Card>

            <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-orange-400" /> Pending Receivables
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">{formatCurrency(summary.receivables)}</div>
              </CardContent>
            </Card>

            <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-purple-500"></div>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-purple-400" /> Total GST Output
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-800">{formatCurrency(summary.total_gst)}</div>
              </CardContent>
            </Card>
          </div>
          
          <div className="w-full">
            <Card className="glass-panel-dark border-slate-200 rounded-2xl p-6">
              <h3 className="font-sans text-lg font-semibold text-slate-800 mb-4">Profit & Loss Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Revenue (Total Sales)</span>
                  <span className="text-slate-800 font-medium">{formatCurrency(summary.total_sales)}</span>
                </div>
                <div className="py-2 border-b border-slate-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600 font-semibold">Direct Costs</span>
                    <span className="text-red-400 font-medium">- {formatCurrency(summary.total_costs)}</span>
                  </div>
                  <div className="pl-4 space-y-2 border-l-2 border-slate-100 ml-2 mt-2 py-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Hotel Accommodations</span>
                      <span className="text-slate-600 font-medium">{formatCurrency(summary.cost_breakdown?.hotel_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Transport</span>
                      <span className="text-slate-600 font-medium">{formatCurrency(summary.cost_breakdown?.transport_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Activities</span>
                      <span className="text-slate-600 font-medium">{formatCurrency(summary.cost_breakdown?.activity_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm italic">
                      <span className="text-slate-400">Other / Misc</span>
                      <span className="text-slate-500">{formatCurrency(summary.total_costs - ((summary.cost_breakdown?.hotel_cost || 0) + (summary.cost_breakdown?.transport_cost || 0) + (summary.cost_breakdown?.activity_cost || 0)))}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Gross Profit</span>
                  <span className="text-green-500 font-bold">{formatCurrency(summary.total_profit)}</span>
                </div>
                <div className="py-2 border-b border-slate-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600 font-semibold">Indirect Expenses</span>
                    <span className="text-orange-400 font-medium">- {formatCurrency(summary.total_indirect_costs || 0)}</span>
                  </div>
                  {Object.keys(indirectCostBreakdown).length > 0 && (
                    <div className="pl-4 space-y-2 border-l-2 border-slate-100 ml-2 mt-2 py-1">
                      {Object.entries(indirectCostBreakdown).map(([category, amount]) => (
                        <div key={category} className="flex justify-between text-sm">
                          <span className="text-slate-500">{category}</span>
                          <span className="text-slate-600 font-medium">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded-lg mt-4">
                  <span className="text-slate-800 font-semibold">Net Profit</span>
                  <span className="text-teal-500 font-bold text-xl">{formatCurrency(summary.total_profit - (summary.total_indirect_costs || 0))}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "invoices" && (
        <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3 text-right">Selling Price</th>
                  <th className="px-4 py-3 text-right">Profit</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No invoices found</td></tr>
                ) : (
                  invoices.map((inv, i) => (
                    <tr key={i} className="border-t border-white/5 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600">{inv.date || "N/A"}</td>
                      <td className="px-4 py-3 text-slate-800 font-medium">
                        {inv.customer_name}
                        <div className="text-xs text-slate-500">Itinerary ID: {inv.itinerary_id?.slice(0, 8)}...</div>
                      </td>
                      <td className="px-4 py-3 text-right text-orange-500 font-medium">{formatCurrency(inv.totalAmount)}</td>
                      <td className="px-4 py-3 text-right text-green-400 font-medium">{formatCurrency(inv.totalProfitGross)}</td>
                      <td className="px-4 py-3 text-center">
                        <a href={`/print/${inv.itinerary_id}`} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="ghost" className="text-teal-400 hover:text-slate-800 hover:bg-teal-500/20 rounded-lg">
                            <Download className="w-4 h-4 mr-1" /> View/PDF
                          </Button>
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {(activeTab === "ledger" || activeTab === "expenses") && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50 border border-slate-200 p-4 rounded-2xl">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <span className="text-slate-500 text-sm">Filter by Party:</span>
                <select 
                  value={selectedParty}
                  onChange={(e) => setSelectedParty(e.target.value)}
                  className="bg-white border border-slate-200 text-slate-800 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-teal-500 w-full sm:w-64"
                >
                  {uniqueParties.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              
              {selectedParty !== "All" && (
                <a href={`/print-ledger?party=${encodeURIComponent(selectedParty)}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-teal-500/50 text-teal-400 hover:bg-teal-500/20 rounded-xl w-full sm:w-auto">
                    <Download className="w-4 h-4 mr-2" /> Download Statement
                  </Button>
                </a>
              )}
              
              <Button onClick={() => setShowAddTx(!showAddTx)} className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl w-full sm:w-auto">
                {showAddTx ? "Cancel" : "+ Add Entry"}
              </Button>
            </div>

            {showAddTx && (
              <Card className="glass-panel-dark border-slate-200 rounded-2xl p-4">
                <form onSubmit={handleAddTransaction} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">
                        {newTx.type === "EXPENSE" ? "Vendor / Payee Name *" : "Party / Customer *"}
                      </label>
                      <input list="parties-list" required type="text" value={newTx.party_name} onChange={e => setNewTx({...newTx, party_name: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" placeholder={newTx.type === "EXPENSE" ? "e.g. Ramesh Properties" : "e.g. Hotel ABC"} />
                      <datalist id="parties-list">
                        {uniqueParties.filter(p => p !== "All").map(p => <option key={p} value={p} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Type *</label>
                      <select required value={newTx.type} onChange={e => setNewTx({...newTx, type: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm">
                        <option value="OUT">Direct Cost Paid (OUT)</option>
                        <option value="IN">Received from Client (IN)</option>
                        <option value="EXPENSE">Indirect Expense (Salary, Rent, etc.)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Amount *</label>
                      <input required type="number" min="0" step="0.01" value={newTx.amount} onChange={e => setNewTx({...newTx, amount: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" placeholder="₹0.00" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Date *</label>
                      <input required type="date" value={newTx.date} onChange={e => setNewTx({...newTx, date: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Bank Account</label>
                      <input type="text" value={newTx.bank_account} onChange={e => setNewTx({...newTx, bank_account: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" placeholder="e.g. HDFC Bank" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">
                        {newTx.type === "EXPENSE" ? "Expense Head / Category *" : "Reference No."}
                      </label>
                      <input list={newTx.type === "EXPENSE" ? "expense-categories" : ""} required={newTx.type === "EXPENSE"} type="text" value={newTx.reference} onChange={e => setNewTx({...newTx, reference: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" placeholder={newTx.type === "EXPENSE" ? "e.g. Salary, Office Rent" : "e.g. UTR / Chq No."} />
                      {newTx.type === "EXPENSE" && (
                        <datalist id="expense-categories">
                          <option value="Salary" />
                          <option value="Office Rent" />
                          <option value="Electricity" />
                          <option value="Marketing & Ads" />
                          <option value="Travel & Transport" />
                          <option value="Office Supplies" />
                          <option value="Repairs & Maintenance" />
                          <option value="Miscellaneous" />
                        </datalist>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-slate-500 block mb-1">Notes / Description</label>
                      <input type="text" value={newTx.notes} onChange={e => setNewTx({...newTx, notes: e.target.value})} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm" placeholder="Any remarks" />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={isSubmitting} className="bg-teal-500 hover:bg-teal-600 text-white rounded-lg px-6">
                      {isSubmitting ? "Saving..." : "Save Transaction"}
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            <Card className="glass-panel-dark border-slate-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Party / Customer</th>
                      <th className="px-4 py-3">Reference / Notes</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayTxs.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No transactions found</td></tr>
                    ) : (
                      displayTxs.map((tx, i) => (
                    <tr key={i} className="border-t border-white/5 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-600">{tx.date || "N/A"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tx.type === "IN" ? "bg-green-100 text-green-700 border border-green-200" : tx.type === "EXPENSE" ? "bg-orange-100 text-orange-700 border border-orange-200" : "bg-red-100 text-red-700 border border-red-200"}`}>
                          {tx.type === "IN" ? "RECEIVED (IN)" : tx.type === "EXPENSE" ? "INDIRECT EXPENSE" : "PAID (OUT)"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-800">
                        {tx.party || tx.customer_name || "Unknown"}
                        <div className="text-xs text-slate-500">Bank: {tx.bankAccount || "N/A"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        <div className="font-medium">
                          {tx.type === "EXPENSE" && tx.reference ? `[${tx.reference}]` : (tx.reference || "—")}
                        </div>
                        <div className="text-xs">{tx.notes || ""}</div>
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${tx.type === "IN" ? "text-green-600" : tx.type === "EXPENSE" ? "text-orange-500" : "text-red-500"}`}>
                        {tx.type === "IN" ? "+" : "-"} {formatCurrency(tx.amount)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => handleEdit(tx)} className="text-blue-500 hover:text-blue-700 mr-3" title="Edit"><Edit className="w-4 h-4 inline"/></button>
                        <button onClick={() => handleDelete(tx.id)} className="text-red-500 hover:text-red-700" title="Delete"><Trash2 className="w-4 h-4 inline"/></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
        </div>
      )}

      {activeTab === "gst" && (
        <Card className="glass-panel-dark border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-sans text-xl font-bold text-slate-800">GST Output Summary</h3>
              <p className="font-sans text-sm text-slate-500">Aggregated GST collected on gross profit across all invoices</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl">
              <div className="text-sm text-slate-500 mb-1">CGST (9%)</div>
              <div className="text-xl font-bold text-slate-800">{formatCurrency(summary.total_gst / 2)}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-xl">
              <div className="text-sm text-slate-500 mb-1">SGST (9%)</div>
              <div className="text-xl font-bold text-slate-800">{formatCurrency(summary.total_gst / 2)}</div>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-xl">
              <div className="text-sm text-purple-300 mb-1">Total GST (18%)</div>
              <div className="text-xl font-bold text-purple-400">{formatCurrency(summary.total_gst)}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
